import * as THREE from 'three';
import type { RigidBody } from '@dimforge/rapier3d-compat';

/**
 * 물리 바디의 상태를 렌더 오브젝트로 옮긴다.
 *
 * 물리는 60Hz 고정, 렌더는 화면 주사율(60/90/120Hz)이다.
 * 물리 상태를 그대로 대입하면 120Hz 화면에서 같은 위치가 두 번 그려져 끊겨 보인다.
 * 직전 스텝과 현재 스텝을 보관해 두고 alpha로 보간해 그 차이를 메운다.
 */
export class BodyView {
  private readonly prevPosition = new THREE.Vector3();
  private readonly prevQuaternion = new THREE.Quaternion();
  private readonly nextPosition = new THREE.Vector3();
  private readonly nextQuaternion = new THREE.Quaternion();

  constructor(
    readonly body: RigidBody,
    readonly object: THREE.Object3D,
  ) {
    this.capture();
    this.prevPosition.copy(this.nextPosition);
    this.prevQuaternion.copy(this.nextQuaternion);
    this.apply(1);
  }

  /** 물리 스텝 직후에 호출한다. */
  sync(): void {
    this.prevPosition.copy(this.nextPosition);
    this.prevQuaternion.copy(this.nextQuaternion);
    this.capture();
  }

  /** 렌더 시점에 호출한다. */
  apply(alpha: number): void {
    this.object.position.lerpVectors(this.prevPosition, this.nextPosition, alpha);
    this.object.quaternion.slerpQuaternions(this.prevQuaternion, this.nextQuaternion, alpha);
  }

  /** 순간이동 직후처럼 보간이 오히려 방해가 될 때 이전 상태를 현재로 맞춘다. */
  snap(): void {
    this.capture();
    this.prevPosition.copy(this.nextPosition);
    this.prevQuaternion.copy(this.nextQuaternion);
    this.apply(1);
  }

  private capture(): void {
    const t = this.body.translation();
    const r = this.body.rotation();
    this.nextPosition.set(t.x, t.y, t.z);
    this.nextQuaternion.set(r.x, r.y, r.z, r.w);
  }
}
